/**
 * Mover documento e pasta arrastando (spec 2026-10-19-kb-arrastar-e-soltar, D4, D7, D8).
 * O arraste só propõe: soltar abre a confirmação, e quem grava são as mesmas rotas dos
 * menus (`kb.document.move`, `kb.folder.update`). A regra do que aceita está em `arrastar.ts`.
 * Arquivo do computador não passa pelo dnd-kit (D5): os alvos usam os eventos nativos e
 * entregam a triagem a `onArquivos`, que abre o envio de hoje.
 */
import { createContext, useContext, useState, type CSSProperties, type DragEvent, type ReactNode } from 'react'
import { Group, Paper, Stack, Text } from '@mantine/core'
import { modals } from '@mantine/modals'
import { notifications } from '@mantine/notifications'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  pointerWithin,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type CollisionDetection,
  type DragEndEvent,
  type DragOverEvent,
  type DragStartEvent,
} from '@dnd-kit/core'
import { IconFileText, IconFolder } from '@tabler/icons-react'
import { updateKbDocument, updateKbFolder, type KbFolderNode } from '@/api/kb'
import { mensagemDeErro, type AcessoDaKb } from './acesso'
import { caminhoDe } from './arvore'
import { useLimitesDaKb } from './limites'
import { idDeArraste, podeArrastar, podeSoltar, type Alvo, type Arrastado, type Veredito } from './arrastar'
import { entradasDoDrop, triar, type Triagem } from './envio'

export const AVISO_DOCUMENTO = 'Quem lê o documento passa a ser quem lê a pasta de destino.'
export const AVISO_PASTA = 'Mover não muda quem lê: cada pasta continua com a própria lista de perfis.'

type EstadoDoAlvo = 'neutro' | 'valido' | 'invalido'

interface Regra {
  nos: KbFolderNode[]
  acesso: AcessoDaKb
  /** Arquivos do computador soltos numa pasta, já triados */
  onArquivos?: (pasta: KbFolderNode, triagem: Triagem) => void
}
// Fora do provider ninguém arrasta: o padrão é de quem só lê.
const RegraDoArraste = createContext<Regra>({ nos: [], acesso: { gere: false, veDetalhes: false, admin: false } })

export function estiloDoAlvo(estado: EstadoDoAlvo): CSSProperties {
  if (estado === 'valido') {
    return { backgroundColor: 'var(--mantine-color-inovareTeal-0)', outline: '1px solid var(--mantine-color-inovareTeal-5)' }
  }
  return estado === 'invalido' ? { cursor: 'not-allowed' } : {}
}

/**
 * `visivel` falso desliga o alvo: a pasta dentro de uma recolhida continua montada no
 * `Collapse`, com o retângulo por cima da linha seguinte, e roubaria a soltura dela.
 */
export function useAlvoDeArraste(alvo: Alvo, visivel = true): { setNodeRef: (el: HTMLElement | null) => void; estado: EstadoDoAlvo; motivo?: string } {
  const { nos, acesso } = useContext(RegraDoArraste)
  const { setNodeRef, isOver, active } = useDroppable({ id: idDeArraste(alvo), data: alvo, disabled: !acesso.gere || !visivel })
  const arrastado = active?.data.current as Arrastado | undefined
  if (!isOver || !arrastado) return { setNodeRef, estado: 'neutro' }
  const v = podeSoltar(arrastado, alvo, nos, acesso)
  return v.aceita ? { setNodeRef, estado: 'valido' } : { setNodeRef, estado: 'invalido', motivo: v.motivo }
}

export function useOrigemDeArraste(a: Arrastado): {
  setNodeRef: (el: HTMLElement | null) => void; attributes: object; listeners: object | undefined; isDragging: boolean
} {
  const { acesso } = useContext(RegraDoArraste)
  const pode = podeArrastar(a, acesso)
  // A linha da tabela continua linha: o padrão do dnd-kit a faria "button".
  const papel = a.tipo === 'documento' ? { role: 'row' } : undefined
  const d = useDraggable({ id: idDeArraste(a), data: a, disabled: !pode, attributes: papel })
  if (!pode) return { setNodeRef: d.setNodeRef, attributes: {}, listeners: undefined, isDragging: false }
  return { setNodeRef: d.setNodeRef, attributes: d.attributes, listeners: d.listeners, isDragging: d.isDragging }
}

const temArquivos = (e: DragEvent) => Array.from(e.dataTransfer?.types ?? []).includes('Files')

/**
 * Alvo de arquivo do computador. Previne o padrão sempre, até sem permissão: sem isso o
 * navegador abre o arquivo na aba. E para a propagação, para a pasta de fora não receber
 * o que caiu na de dentro (D7).
 */
export function useAlvoDeArquivo(pasta: KbFolderNode): {
  estado: EstadoDoAlvo
  handlers: { onDragOver: (e: DragEvent) => void; onDragLeave: (e: DragEvent) => void; onDrop: (e: DragEvent) => void }
} {
  const { acesso, onArquivos } = useContext(RegraDoArraste)
  const { maxMb } = useLimitesDaKb()
  const [sobre, setSobre] = useState(false)
  const pode = !!onArquivos && acesso.gere && (pasta.organization_id !== null || acesso.admin)
  return {
    estado: sobre ? (pode ? 'valido' : 'invalido') : 'neutro',
    handlers: {
      onDragOver: (e) => {
        if (!temArquivos(e)) return
        e.preventDefault(); e.stopPropagation()
        e.dataTransfer.dropEffect = pode ? 'copy' : 'none'
        setSobre(true)
      },
      onDragLeave: (e) => {
        if (!temArquivos(e) || e.currentTarget.contains(e.relatedTarget as Node | null)) return
        setSobre(false)
      },
      onDrop: (e) => {
        if (!temArquivos(e)) return
        e.preventDefault(); e.stopPropagation()
        setSobre(false)
        if (pode && onArquivos) onArquivos(pasta, triar(entradasDoDrop(e.dataTransfer), maxMb))
      },
    },
  }
}

/** Um nó só recebe as duas refs (alvo e origem). */
export function juntarRefs(...refs: ((el: HTMLElement | null) => void)[]) {
  return (el: HTMLElement | null) => refs.forEach((r) => r(el))
}

// Alvos podem se sobrepor (a linha da raiz e a da pasta): vale o menor sob o ponteiro.
const oMenorSobOPonteiro: CollisionDetection = (args) => {
  const area = (id: string | number) => {
    const r = args.droppableRects.get(id)
    return r ? r.width * r.height : Infinity
  }
  return pointerWithin(args).sort((a, b) => area(a.id) - area(b.id))
}

function nomeDe(a: Arrastado): string {
  return a.tipo === 'documento' ? a.doc.titulo : a.pasta.nome
}

interface Props extends Regra {
  /** Nome que a organização tem na árvore, para a confirmação de "mover para o topo" */
  rotuloDaRaiz: (organizationId: number | null) => string
  children: ReactNode
}

export function ArrastarESoltar({ nos, acesso, onArquivos, rotuloDaRaiz, children }: Props) {
  const qc = useQueryClient()
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }))
  const [arrastado, setArrastado] = useState<Arrastado | null>(null)
  const [veredito, setVeredito] = useState<Veredito | null>(null)

  const caminhoDoAlvo = (alvo: Alvo) =>
    alvo.tipo === 'pasta' ? caminhoDe(nos, alvo.pasta.id) : rotuloDaRaiz(alvo.organizationId)
  const caminhoDaOrigem = (a: Arrastado) => {
    if (a.tipo === 'pasta') return caminhoDe(nos, a.pasta.id)
    return a.doc.folder_id === null ? rotuloDaRaiz(a.doc.organization_id) : caminhoDe(nos, a.doc.folder_id)
  }

  const mover = useMutation({
    mutationFn: async ({ a, alvo }: { a: Arrastado; alvo: Alvo }) => {
      const destino = alvo.tipo === 'pasta' ? alvo.pasta.id : null
      if (a.tipo === 'documento') {
        if (destino === null) throw new Error('Escolha uma pasta')
        await updateKbDocument(a.doc.id, { folder_id: destino })
      } else {
        await updateKbFolder(a.pasta.id, { parent_id: destino })
      }
    },
    onSettled: () => {
      for (const k of ['kb-folders', 'kb-documents', 'kb-folder', 'kb-folder-detalhes']) qc.invalidateQueries({ queryKey: [k] })
    },
    onSuccess: (_r, { alvo }) => notifications.show({ color: 'teal', title: 'Movido', message: `Movido para ${caminhoDoAlvo(alvo)}` }),
    onError: (e: unknown) => notifications.show({ color: 'red', title: 'Não foi possível mover', message: mensagemDeErro(e, 'Falha ao mover.') }),
  })

  const confirmar = (a: Arrastado, alvo: Alvo) =>
    modals.openConfirmModal({
      title: a.tipo === 'documento' ? 'Mover documento' : 'Mover pasta',
      children: (
        <Stack gap="xs">
          <Text size="sm" fw={600}>{nomeDe(a)}</Text>
          <Text size="sm" ff="monospace">{caminhoDaOrigem(a)} → {caminhoDoAlvo(alvo)}</Text>
          <Text size="xs" c="dimmed">{a.tipo === 'documento' ? AVISO_DOCUMENTO : AVISO_PASTA}</Text>
        </Stack>
      ),
      labels: { confirm: 'Mover', cancel: 'Cancelar' },
      confirmProps: { 'data-autofocus': 'true' },
      onConfirm: () => mover.mutate({ a, alvo }),
    })

  const limpar = () => { setArrastado(null); setVeredito(null) }

  const aoComecar = (e: DragStartEvent) => setArrastado((e.active.data.current as Arrastado | undefined) ?? null)
  const aoPassar = (e: DragOverEvent) => {
    const a = e.active.data.current as Arrastado | undefined
    const alvo = e.over?.data.current as Alvo | undefined
    setVeredito(a && alvo ? podeSoltar(a, alvo, nos, acesso) : null)
  }
  const aoSoltar = (e: DragEndEvent) => {
    limpar()
    const a = e.active.data.current as Arrastado | undefined
    const alvo = e.over?.data.current as Alvo | undefined
    if (!a || !alvo || !podeSoltar(a, alvo, nos, acesso).aceita) return
    confirmar(a, alvo)
  }

  return (
    <RegraDoArraste.Provider value={{ nos, acesso, onArquivos }}>
      <DndContext sensors={sensors} collisionDetection={oMenorSobOPonteiro}
        onDragStart={aoComecar} onDragOver={aoPassar} onDragEnd={aoSoltar} onDragCancel={limpar}>
        {/* Arquivo solto fora de um alvo não abre na aba. */}
        <div onDragOver={(e) => { if (temArquivos(e)) e.preventDefault() }} onDrop={(e) => { if (temArquivos(e)) e.preventDefault() }}>
          {children}
        </div>
        <DragOverlay dropAnimation={null}>
          {arrastado && (
            <Paper withBorder shadow="sm" px="xs" py={4} radius="sm" style={{ cursor: 'grabbing', width: 'max-content', maxWidth: 320 }}>
              <Group gap={6} wrap="nowrap">
                {arrastado.tipo === 'documento' ? <IconFileText size={14} stroke={1.5} /> : <IconFolder size={14} stroke={1.5} />}
                <Text size="sm" truncate>{nomeDe(arrastado)}</Text>
              </Group>
              {veredito && !veredito.aceita && <Text size="xs" c="dimmed">{veredito.motivo}</Text>}
            </Paper>
          )}
        </DragOverlay>
      </DndContext>
    </RegraDoArraste.Provider>
  )
}
